import React, { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "Maj", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dec"];

/**
 * Dialog til at administrere røde/gule/grønne tærskler for omsætnings-fanen.
 *
 * Tre tabs:
 *  1) Default — ét sæt tærskler der gælder alle celler hvor intet andet er sat
 *  2) Pr. måned — overrides for specifikke måneder (multi-select)
 *  3) Pr. bil — overrides for enkelte biler (multi-select)
 *
 * Prioritet ved farvelægning: Bil-override > Måneds-override > Default.
 */
export default function RevenueThresholdsDialog({ open, onOpenChange, year, settings, cars, onSaved }) {
  const [tab, setTab] = useState("default");

  // Default
  const [defRed, setDefRed] = useState("");
  const [defGreen, setDefGreen] = useState("");

  // Pr. måned (kun for det valgte år)
  const [monthOverrides, setMonthOverrides] = useState({}); // {0..11: {red, green} | null}
  const [selectedMonths, setSelectedMonths] = useState({}); // {0..11: bool}
  const [bulkMonthRed, setBulkMonthRed] = useState("");
  const [bulkMonthGreen, setBulkMonthGreen] = useState("");

  // Pr. bil
  const [carOverrides, setCarOverrides] = useState({}); // {car_id: {red, green} | null}
  const [selectedCars, setSelectedCars] = useState({});  // {car_id: bool}
  const [bulkCarRed, setBulkCarRed] = useState("");
  const [bulkCarGreen, setBulkCarGreen] = useState("");
  const [carSearch, setCarSearch] = useState("");

  useEffect(() => {
    if (!open || !settings) return;
    const def = settings.revenue_thresholds || { red: 500, green: 1000 };
    setDefRed(String(def.red ?? 500));
    setDefGreen(String(def.green ?? 1000));

    const byMonth = settings.revenue_thresholds_by_month || {};
    const monthMap = {};
    for (let i = 0; i < 12; i += 1) {
      const key = `${year}-${String(i + 1).padStart(2, "0")}`;
      monthMap[i] = byMonth[key] || null;
    }
    setMonthOverrides(monthMap);
    setSelectedMonths({});
    setBulkMonthRed("");
    setBulkMonthGreen("");

    const carMap = {};
    (cars || []).forEach((c) => {
      carMap[c.id] = c.revenue_thresholds || null;
    });
    setCarOverrides(carMap);
    setSelectedCars({});
    setBulkCarRed("");
    setBulkCarGreen("");
    setCarSearch("");
  }, [open, settings, cars, year]);

  const filteredCars = useMemo(() => {
    const s = carSearch.toLowerCase().trim();
    const arr = (cars || []).filter((c) => c.status !== "sold");
    if (!s) return arr;
    return arr.filter((c) =>
      `${c.license_plate} ${c.make || ""} ${c.model || ""}`.toLowerCase().includes(s)
    );
  }, [cars, carSearch]);

  // Bulk-handlinger pr. måned
  const monthIdsSelected = Object.keys(selectedMonths).filter((k) => selectedMonths[k]).map(Number);
  const applyBulkMonth = () => {
    const r = bulkMonthRed === "" ? null : Number(bulkMonthRed);
    const g = bulkMonthGreen === "" ? null : Number(bulkMonthGreen);
    if (r == null && g == null) {
      toast.error("Indtast mindst én værdi");
      return;
    }
    if (r != null && g != null && r >= g) {
      toast.error("Rød skal være mindre end grøn");
      return;
    }
    setMonthOverrides((m) => {
      const next = { ...m };
      monthIdsSelected.forEach((idx) => {
        const cur = next[idx] || { red: Number(defRed) || 500, green: Number(defGreen) || 1000 };
        next[idx] = { red: r != null ? r : cur.red, green: g != null ? g : cur.green };
      });
      return next;
    });
    setSelectedMonths({});
    setBulkMonthRed("");
    setBulkMonthGreen("");
  };
  const clearMonthOverrides = () => {
    setMonthOverrides((m) => {
      const next = { ...m };
      monthIdsSelected.forEach((idx) => { next[idx] = null; });
      return next;
    });
    setSelectedMonths({});
  };

  // Bulk-handlinger pr. bil
  const carIdsSelected = Object.keys(selectedCars).filter((k) => selectedCars[k]);
  const applyBulkCar = () => {
    const r = bulkCarRed === "" ? null : Number(bulkCarRed);
    const g = bulkCarGreen === "" ? null : Number(bulkCarGreen);
    if (r == null && g == null) {
      toast.error("Indtast mindst én værdi");
      return;
    }
    if (r != null && g != null && r >= g) {
      toast.error("Rød skal være mindre end grøn");
      return;
    }
    setCarOverrides((m) => {
      const next = { ...m };
      carIdsSelected.forEach((id) => {
        const cur = next[id] || { red: Number(defRed) || 500, green: Number(defGreen) || 1000 };
        next[id] = { red: r != null ? r : cur.red, green: g != null ? g : cur.green };
      });
      return next;
    });
    setSelectedCars({});
    setBulkCarRed("");
    setBulkCarGreen("");
  };
  const clearCarOverrides = () => {
    setCarOverrides((m) => {
      const next = { ...m };
      carIdsSelected.forEach((id) => { next[id] = null; });
      return next;
    });
    setSelectedCars({});
  };

  const save = async () => {
    const r = Number(defRed) || 500;
    const g = Number(defGreen) || 1000;
    if (r >= g) {
      toast.error("Default: rød skal være mindre end grøn");
      return;
    }
    // Byg by_month-dict — bevar overrides for andre år, opdater kun det valgte år
    const existing = { ...(settings.revenue_thresholds_by_month || {}) };
    for (let i = 0; i < 12; i += 1) {
      const key = `${year}-${String(i + 1).padStart(2, "0")}`;
      const v = monthOverrides[i];
      if (v == null) {
        delete existing[key];
      } else {
        existing[key] = { red: Number(v.red), green: Number(v.green) };
      }
    }
    const payload = {
      ...settings,
      revenue_thresholds: { red: r, green: g },
      revenue_thresholds_by_month: existing,
    };
    delete payload.id;
    try {
      await api.put("/settings", payload);
      // Gem bil-overrides — kun de biler der har ændret sig
      const original = {};
      (cars || []).forEach((c) => { original[c.id] = c.revenue_thresholds || null; });
      const carPatches = [];
      Object.keys(carOverrides).forEach((id) => {
        const cur = carOverrides[id];
        const orig = original[id];
        const same = JSON.stringify(cur) === JSON.stringify(orig);
        if (!same) {
          carPatches.push({ id, value: cur });
        }
      });
      for (const patch of carPatches) {
        await api.patch(`/cars/${patch.id}`, { revenue_thresholds: patch.value });
      }
      toast.success("Tærskler gemt");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const tabBtnCls = (k) =>
    "px-3 py-1.5 text-xs uppercase tracking-wider transition-colors " +
    (tab === k ? "bg-[#C8A661] text-black font-semibold" : "bg-zinc-800 text-zinc-400 hover:text-white");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="thresholds-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-2xl">Tærskler · {year}</DialogTitle>
          <p className="text-xs text-zinc-500 mt-1">
            Sæt grænserne for rød · gul · grøn baseret på reel indtjening (ex. moms minus faste omk.).
            Pr. bil overskriver pr. måned, som overskriver default.
          </p>
        </DialogHeader>

        <div className="flex gap-1 mb-4">
          <button type="button" className={tabBtnCls("default")} onClick={() => setTab("default")} data-testid="threshold-tab-default">Default</button>
          <button type="button" className={tabBtnCls("month")} onClick={() => setTab("month")} data-testid="threshold-tab-month">Pr. måned</button>
          <button type="button" className={tabBtnCls("car")} onClick={() => setTab("car")} data-testid="threshold-tab-car">Pr. bil</button>
        </div>

        {tab === "default" && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="overline text-[10px] text-red-400">🔴 Rød hvis under</Label>
              <Input
                type="number"
                value={defRed}
                onChange={(e) => setDefRed(e.target.value)}
                className="rounded-none border-zinc-700 mt-1"
                data-testid="default-red-input"
              />
            </div>
            <div>
              <Label className="overline text-[10px] text-emerald-400">🟢 Grøn hvis over</Label>
              <Input
                type="number"
                value={defGreen}
                onChange={(e) => setDefGreen(e.target.value)}
                className="rounded-none border-zinc-700 mt-1"
                data-testid="default-green-input"
              />
            </div>
            <div className="col-span-2 text-xs text-zinc-500">
              Beløb mellem rød og grøn vises som 🟡 gul. Alle værdier er ex. moms.
            </div>
          </div>
        )}

        {tab === "month" && (
          <div>
            <div className="bg-zinc-900/60 border border-zinc-800 p-3 mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
              <div className="col-span-2 sm:col-span-1 text-xs text-zinc-400">
                {monthIdsSelected.length} valgt
              </div>
              <Input type="number" placeholder="Rød <" value={bulkMonthRed} onChange={(e) => setBulkMonthRed(e.target.value)} className="rounded-none border-zinc-700 text-xs h-8" data-testid="bulk-month-red" />
              <Input type="number" placeholder="Grøn >" value={bulkMonthGreen} onChange={(e) => setBulkMonthGreen(e.target.value)} className="rounded-none border-zinc-700 text-xs h-8" data-testid="bulk-month-green" />
              <div className="flex gap-1">
                <Button type="button" disabled={monthIdsSelected.length === 0} onClick={applyBulkMonth} className="rounded-none bg-[#C8A661] hover:bg-[#D4B574] text-black h-8 text-xs flex-1" data-testid="bulk-month-apply">Anvend</Button>
                <Button type="button" disabled={monthIdsSelected.length === 0} onClick={clearMonthOverrides} variant="outline" className="rounded-none border-zinc-700 h-8 text-xs">Nulstil</Button>
              </div>
            </div>
            <table className="w-full text-xs">
              <thead className="text-zinc-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2 w-8"></th>
                  <th className="text-left py-2">Måned</th>
                  <th className="text-right py-2">Rød &lt;</th>
                  <th className="text-right py-2">Grøn &gt;</th>
                  <th className="text-right py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {MONTHS.map((m, i) => {
                  const ov = monthOverrides[i];
                  return (
                    <tr key={m} className="border-b border-zinc-900">
                      <td className="py-1.5">
                        <Checkbox
                          checked={!!selectedMonths[i]}
                          onCheckedChange={(v) => setSelectedMonths((s) => ({ ...s, [i]: !!v }))}
                          data-testid={`select-month-${i}`}
                        />
                      </td>
                      <td className="py-1.5 font-display">{m} {year}</td>
                      <td className="py-1.5 text-right tabular-nums text-red-400">
                        {ov ? ov.red : <span className="text-zinc-600">{defRed}</span>}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-emerald-400">
                        {ov ? ov.green : <span className="text-zinc-600">{defGreen}</span>}
                      </td>
                      <td className="py-1.5 text-right text-[10px] uppercase tracking-wider">
                        {ov ? <span className="text-[#C8A661]">override</span> : <span className="text-zinc-600">default</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {tab === "car" && (
          <div>
            <div className="bg-zinc-900/60 border border-zinc-800 p-3 mb-3 grid grid-cols-2 sm:grid-cols-4 gap-2 items-end">
              <div className="col-span-2 sm:col-span-1 text-xs text-zinc-400">
                {carIdsSelected.length} valgt
              </div>
              <Input type="number" placeholder="Rød <" value={bulkCarRed} onChange={(e) => setBulkCarRed(e.target.value)} className="rounded-none border-zinc-700 text-xs h-8" data-testid="bulk-car-red" />
              <Input type="number" placeholder="Grøn >" value={bulkCarGreen} onChange={(e) => setBulkCarGreen(e.target.value)} className="rounded-none border-zinc-700 text-xs h-8" data-testid="bulk-car-green" />
              <div className="flex gap-1">
                <Button type="button" disabled={carIdsSelected.length === 0} onClick={applyBulkCar} className="rounded-none bg-[#C8A661] hover:bg-[#D4B574] text-black h-8 text-xs flex-1" data-testid="bulk-car-apply">Anvend</Button>
                <Button type="button" disabled={carIdsSelected.length === 0} onClick={clearCarOverrides} variant="outline" className="rounded-none border-zinc-700 h-8 text-xs">Nulstil</Button>
              </div>
            </div>
            <Input
              placeholder="Søg plade, mærke, model…"
              value={carSearch}
              onChange={(e) => setCarSearch(e.target.value)}
              className="rounded-none border-zinc-700 mb-3 h-8 text-xs"
              data-testid="threshold-car-search"
            />
            <table className="w-full text-xs">
              <thead className="text-zinc-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left py-2 w-8"></th>
                  <th className="text-left py-2">Bil</th>
                  <th className="text-right py-2">Rød &lt;</th>
                  <th className="text-right py-2">Grøn &gt;</th>
                  <th className="text-right py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredCars.map((c) => {
                  const ov = carOverrides[c.id];
                  return (
                    <tr key={c.id} className="border-b border-zinc-900">
                      <td className="py-1.5">
                        <Checkbox
                          checked={!!selectedCars[c.id]}
                          onCheckedChange={(v) => setSelectedCars((s) => ({ ...s, [c.id]: !!v }))}
                          data-testid={`select-car-threshold-${c.license_plate}`}
                        />
                      </td>
                      <td className="py-1.5">
                        <span className="font-display font-bold text-white">{c.license_plate}</span>
                        <span className="text-zinc-500 ml-2">{c.make} {c.model}</span>
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-red-400">
                        {ov ? ov.red : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="py-1.5 text-right tabular-nums text-emerald-400">
                        {ov ? ov.green : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="py-1.5 text-right text-[10px] uppercase tracking-wider">
                        {ov ? <span className="text-[#C8A661]">override</span> : <span className="text-zinc-600">default</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <DialogFooter className="gap-2 mt-4">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-none border-zinc-700">Annullér</Button>
          <Button type="button" onClick={save} className="rounded-none bg-[#C8A661] hover:bg-[#D4B574] text-black font-semibold" data-testid="thresholds-save">Gem tærskler</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
